// Importing files into an encrypted library (docs/STORAGE.md §3): any file, with folder
// structure kept. Each file is read on this device (type, and for audio and video whether
// the browser plays it, tags, duration and cover art), encrypted (hearth-att v1), described
// by an encrypted entry, and uploaded part by part: through the server for an on-disk
// location, straight to the bucket for S3. Media browsers can't play (and, if asked, large
// originals) also gets a playback copy made on this device (./playbackCopy.ts); the
// original is always kept as it is.

import { blobPath, completeBody, partsOf, presign, uploadIdOf, writeTarget, type AccessKey, type Bucket } from './sealedStorage'
import type { BlobRole, BlobUpload, LibraryBlob, LibraryEntry } from '../api/types'
import { ciphertextSize, fileEncryptor, TAG_SIZE, type FileEncryptor } from './attachments'
import { encryptEntryMeta, type EntryKind, type EntryMeta, type EntryTags } from './libraryEntries'
import { browserCopies, planCopy, type CopyMaker, type CopyMode, type CopyPlan, type MadeCopy } from './playbackCopy'

/** The path a file gets in the library: its folder-relative path, minus hidden parts. */
export function libraryPath(rel: string): string {
  return rel
    .split(/[/\\]+/)
    .filter((p) => p && p !== '.' && p !== '..')
    .join('/')
}

/** Hidden files and folders (.DS_Store, .git, …) aren't imported. */
function hidden(path: string): boolean {
  return path.split('/').some((p) => p.startsWith('.'))
}

export class UploadCancelled extends Error {
  constructor() {
    super('cancelled')
  }
}

export interface PickedFile {
  file: File
  path: string
}

/** Files from an <input> (with webkitRelativePath for folder picks). */
export function fromInput(files: FileList | File[]): { files: PickedFile[]; skipped: number } {
  const out: PickedFile[] = []
  let skipped = 0
  for (const f of Array.from(files)) {
    const path = libraryPath(f.webkitRelativePath || f.name)
    if (path && !hidden(path)) out.push({ file: f, path })
    else skipped++
  }
  return { files: out, skipped }
}

/** Files from a drop, walking dropped folders (Chromium/Firefox/Safari entries API). */
export async function fromDrop(dt: DataTransfer): Promise<{ files: PickedFile[]; skipped: number }> {
  const entries = Array.from(dt.items)
    .map((i) => i.webkitGetAsEntry?.())
    .filter((e): e is FileSystemEntry => !!e)
  if (entries.length === 0) return fromInput(dt.files)
  const all: PickedFile[] = []
  const walk = async (entry: FileSystemEntry): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej))
      all.push({ file, path: libraryPath(entry.fullPath) })
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      // readEntries returns batches until empty.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej))
        if (batch.length === 0) break
        for (const e of batch) await walk(e)
      }
    }
  }
  for (const e of entries) await walk(e)
  const files = all.filter((f) => f.path && !hidden(f.path))
  return { files, skipped: all.length - files.length }
}

// --- What a file is --------------------------------------------------------------------

const MIME_BY_EXT: Record<string, string> = {
  mp3: 'audio/mpeg',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  md: 'text/markdown',
}

export function mimeOf(file: { name: string; type: string }): string {
  if (file.type) return file.type
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return MIME_BY_EXT[ext] ?? 'application/octet-stream'
}

export function kindOf(mime: string): EntryKind {
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/')) return 'video'
  return 'file'
}

/** Whether this browser plays a file as it is, and how long it runs. */
export type Probe = (file: File, kind: 'audio' | 'video') => Promise<{ playable: boolean; duration?: number }>

export const browserProbe: Probe = (file, kind) =>
  new Promise((resolve) => {
    const el = document.createElement(kind)
    const url = URL.createObjectURL(file)
    const finish = (r: { playable: boolean; duration?: number }) => {
      clearTimeout(timer)
      el.removeAttribute('src')
      el.load()
      URL.revokeObjectURL(url)
      resolve(r)
    }
    const timer = setTimeout(() => finish({ playable: false }), 8000)
    el.preload = 'metadata'
    el.onloadedmetadata = () => {
      const video = el as HTMLVideoElement
      // A video whose picture the browser can't decode shows as 0×0.
      const ok = kind === 'audio' || video.videoWidth > 0
      finish({ playable: ok, duration: Number.isFinite(el.duration) ? el.duration : undefined })
    }
    el.onerror = () => finish({ playable: false })
    el.src = url
  })

export interface MediaInfo {
  mime: string
  kind: EntryKind
  streamable: boolean
  tags?: EntryTags
  art?: { data: Uint8Array; mime: string }
}

/** A small JPEG of an image for grids (HRTH-164.3), or null where it can't be drawn. */
export type Thumbnailer = (file: Blob) => Promise<Blob | null>

export const PREVIEW_PX = 480

export const browserThumbnail: Thumbnailer = async (file) => {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null
  try {
    const bmp = await createImageBitmap(file)
    const scale = Math.min(1, PREVIEW_PX / Math.max(bmp.width, bmp.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bmp.width * scale))
    canvas.height = Math.max(1, Math.round(bmp.height * scale))
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    bmp.close()
    return await new Promise((done) => canvas.toBlob(done, 'image/jpeg', 0.8))
  } catch {
    return null
  }
}

/** Reads what a file is on this device; tags and art for audio and video. */
export async function readMediaInfo(file: File, probe: Probe = browserProbe): Promise<MediaInfo> {
  const mime = mimeOf(file)
  const kind = kindOf(mime)
  if (kind !== 'audio' && kind !== 'video') return { mime, kind, streamable: false }
  const [played, parsed] = await Promise.all([
    probe(file, kind).catch(() => ({ playable: false, duration: undefined })),
    import('music-metadata').then((mm) => mm.parseBlob(file, { duration: false })).catch(() => null),
  ])
  const c = parsed?.common
  const tags: EntryTags = {}
  if (c?.title) tags.title = c.title
  if (c?.artist) tags.artist = c.artist
  if (c?.album) tags.album = c.album
  if (c?.track?.no) tags.track = c.track.no
  const duration = played.duration ?? parsed?.format.duration
  if (duration) tags.duration = Math.round(duration * 10) / 10
  const pic = c?.picture?.[0]
  return {
    mime,
    kind,
    streamable: played.playable,
    ...(Object.keys(tags).length ? { tags } : {}),
    ...(pic ? { art: { data: pic.data, mime: pic.format || 'image/jpeg' } } : {}),
  }
}

// --- Uploading blobs -------------------------------------------------------------------

export interface BlobApi {
  startBlobUpload(entry: string, role: BlobRole, size: number): Promise<BlobUpload>
  blobUpload(id: string): Promise<BlobUpload>
  blobUploadPart(id: string, offset: number, part: Blob): Promise<BlobUpload>
  blobPartUrl(id: string, n: number): Promise<string>
  reportBlobPart(id: string, n: number, etag: string, size: number): Promise<BlobUpload>
  completeBlobUpload(id: string): Promise<LibraryBlob>
  cancelBlobUpload(id: string): Promise<void>
  /** `PUT`s a part to a signed bucket URL; returns the bucket's ETag. */
  putToBucket(url: string, body: Blob): Promise<string>
  /** Relay mode: a request signed here, sent through the server. */
  relay(method: string, signed: string, body?: Blob | string): Promise<{ status: number; etag: string | null; text: string }>
  registerMultipart(id: string, uploadId: string): Promise<BlobUpload>
}

/** Where a library in a sealed bucket keeps its files, and the key to write them (STORAGE.md §11.2). */
export interface RelayTarget {
  library: string
  bucket: Bucket
  write: AccessKey
}

/** A bucket's answer that isn't a success, as an error the retry logic understands. */
function bucketError(what: string, status: number, text: string): Error {
  return Object.assign(new Error(`the bucket refused ${what} (${status})${text.includes('<Message>') ? `: ${/<Message>([^<]*)/.exec(text)?.[1]}` : ''}`), {
    status,
  })
}

/** Ciphertext bytes [start, end) of an encrypted file, encrypting only the chunks they cover. */
export async function ciphertextSlice(enc: FileEncryptor, start: number, end: number): Promise<Blob> {
  const ct = enc.info.chunk + TAG_SIZE
  const first = Math.floor(start / ct)
  const last = Math.floor((end - 1) / ct)
  const parts: ArrayBuffer[] = []
  for (let i = first; i <= last; i++) parts.push(await enc.encryptChunk(i))
  return new Blob(parts).slice(start - first * ct, end - first * ct)
}

interface UploadOpts {
  signal?: AbortSignal
  onProgress?: (fraction: number) => void
  retries?: number
  wait?: (ms: number) => Promise<void>
}

interface BlobOpts extends UploadOpts {
  /** An upload started before (e.g. before a reload) to carry on with. */
  resume?: string
  /** Told the upload's id once there is one, so it can be resumed later. */
  onStart?: (upload: string) => void | Promise<void>
  /** For a library in a sealed bucket: where, and the key to write (none: read-only here). */
  relay?: RelayTarget | null
}

/** Uploads one encrypted blob for an entry, resuming parts after network trouble. */
export async function uploadBlob(
  api: BlobApi,
  entry: string,
  role: BlobRole,
  enc: FileEncryptor,
  opts: BlobOpts = {},
): Promise<LibraryBlob> {
  const total = ciphertextSize(enc.info.size, enc.info.chunk)
  const retries = opts.retries ?? 8
  const wait = opts.wait ?? ((ms) => new Promise<void>((r) => setTimeout(r, ms)))
  // One the server has let go of (idle for a day) starts over, under the same key.
  let up = (opts.resume ? await api.blobUpload(opts.resume).catch(() => null) : null) ?? (await api.startBlobUpload(entry, role, total))
  await opts.onStart?.(up.id)
  const size = up.part_size
  const cancel = async (): Promise<never> => {
    await api.cancelBlobUpload(up.id).catch(() => undefined)
    throw new UploadCancelled()
  }
  /** Runs one step, retrying network trouble; limits and permissions fail right away. */
  const attempt = async (step: () => Promise<void>): Promise<void> => {
    for (let failures = 1; ; failures++) {
      if (opts.signal?.aborted) await cancel()
      try {
        return await step()
      } catch (e) {
        const status = (e as { status?: number }).status ?? 0
        if (failures > retries || (status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429)) {
          await api.cancelBlobUpload(up.id).catch(() => undefined)
          throw e
        }
        await wait(Math.min(30_000, 500 * 2 ** failures))
        up = await api.blobUpload(up.id).catch(() => up)
      }
    }
  }
  if (up.mode === 'relay') {
    const r = opts.relay ?? writeTarget(up.library)
    if (!r) {
      await api.cancelBlobUpload(up.id).catch(() => undefined)
      throw new Error('This library’s storage is read-only here: there’s no write key for it on this device')
    }
    const path = blobPath(r.library, up.blob)
    const sign = (method: string, query: Record<string, string> = {}) => presign(r.bucket, r.write, method, path, query)
    if (!up.multipart) {
      await attempt(async () => {
        const res = await api.relay('POST', sign('POST', { uploads: '' }))
        const id = res.status === 200 ? uploadIdOf(res.text) : null
        if (!id) throw bucketError('starting the upload', res.status, res.text)
        up = await api.registerMultipart(up.id, id)
      })
    }
    const multipart = up.multipart!
    const count = Math.ceil(total / size)
    for (let n = 1; n <= count; n++) {
      if (up.parts.includes(n)) continue
      await attempt(async () => {
        const body = await ciphertextSlice(enc, (n - 1) * size, Math.min(total, n * size))
        const res = await api.relay('PUT', sign('PUT', { partNumber: String(n), uploadId: multipart }), body)
        if (res.status !== 200 || !res.etag) throw bucketError(`part ${n}`, res.status, res.text)
        up = await api.reportBlobPart(up.id, n, res.etag, body.size)
      })
      opts.onProgress?.(Math.min(1, (up.parts.length * size) / total))
    }
    if (opts.signal?.aborted) await cancel()
    // The parts' ETags, from the bucket (parts sent before a reload included).
    const etags = new Map<number, string>()
    let marker: string | null = '0'
    while (marker !== null) {
      const from: string = marker
      await attempt(async () => {
        const res = await api.relay('GET', sign('GET', { uploadId: multipart, 'part-number-marker': from }))
        if (res.status !== 200) throw bucketError('listing the parts', res.status, res.text)
        const page = partsOf(res.text)
        for (const [n, e] of page.parts) etags.set(n, e)
        marker = page.next
      })
    }
    const ordered = Array.from({ length: count }, (_, i) => etags.get(i + 1) ?? '')
    if (ordered.some((e) => !e)) throw new Error('some parts never reached the bucket; upload the file again')
    await attempt(async () => {
      const res = await api.relay('POST', sign('POST', { uploadId: multipart }), completeBody(ordered))
      if (res.status !== 200 || res.text.includes('<Error>')) throw bucketError('finishing the upload', res.status, res.text)
    })
    return api.completeBlobUpload(up.id)
  }
  if (up.mode === 'server') {
    while (up.received < total) {
      await attempt(async () => {
        const end = Math.min(total, up.received + size)
        up = await api.blobUploadPart(up.id, up.received, await ciphertextSlice(enc, up.received, end))
      })
      opts.onProgress?.(up.received / total)
    }
  } else {
    const count = Math.ceil(total / size)
    for (let n = 1; n <= count; n++) {
      if (up.parts.includes(n)) continue
      await attempt(async () => {
        const body = await ciphertextSlice(enc, (n - 1) * size, Math.min(total, n * size))
        const etag = await api.putToBucket(await api.blobPartUrl(up.id, n), body)
        up = await api.reportBlobPart(up.id, n, etag, body.size)
      })
      opts.onProgress?.(Math.min(1, (up.parts.length * size) / total))
    }
  }
  if (opts.signal?.aborted) await cancel()
  return api.completeBlobUpload(up.id)
}

// --- Importing a file ------------------------------------------------------------------

export interface ImportApi extends BlobApi {
  addLibraryEntry(library: string, meta: string): Promise<LibraryEntry>
  updateLibraryEntry(entry: string, meta: string): Promise<LibraryEntry>
  deleteLibraryEntry(entry: string): Promise<void>
}

/**
 * An import that hasn't finished, remembered on this device so it can carry on after a
 * reload: browsers can't reopen a picked file, so the person picks it again, and its
 * remaining parts are encrypted under the same key (the bytes must be the same).
 */
export interface PendingImport {
  library: string
  entry: string
  path: string
  size: number
  modified: number
  /** SHA-256 of the file's first and last chunks. */
  fingerprint: string
  meta: EntryMeta
  uploads: Partial<Record<BlobRole, string>>
  done: BlobRole[]
  /** The playback copy being uploaded: how it was made, and where it's kept on this device. */
  copy?: { plan: CopyPlan; name?: string }
}

export interface PendingStore {
  list(): Promise<PendingImport[]>
  put(p: PendingImport): Promise<void>
  remove(entry: string): Promise<void>
}

/** Pending imports in this device's own storage (never sent anywhere: they hold file keys). */
export function kvPendingStore(kv: { getKv<T>(key: string): Promise<T | undefined>; setKv(key: string, value: unknown): Promise<void> }): PendingStore {
  const KEY = 'pendingImports'
  // Writes queue up so concurrent uploads don't lose each other's updates.
  let queue: Promise<unknown> = Promise.resolve()
  const change = (fn: (all: Record<string, PendingImport>) => void) =>
    (queue = queue.then(async () => {
      const all = (await kv.getKv<Record<string, PendingImport>>(KEY)) ?? {}
      fn(all)
      await kv.setKv(KEY, all)
    }))
  return {
    list: async () => {
      await queue.catch(() => undefined)
      return Object.values((await kv.getKv<Record<string, PendingImport>>(KEY)) ?? {})
    },
    // Plain JSON copies: records may come from reactive (proxied) state.
    put: (p) => change((all) => void (all[p.entry] = JSON.parse(JSON.stringify(p)) as PendingImport)) as Promise<void>,
    remove: (entry) => change((all) => void delete all[entry]) as Promise<void>,
  }
}

export async function fingerprint(file: Blob, chunk: number): Promise<string> {
  const part = async (b: Blob) => new Uint8Array(await crypto.subtle.digest('SHA-256', await b.arrayBuffer()))
  const [head, tail] = await Promise.all([part(file.slice(0, chunk)), part(file.slice(Math.max(0, file.size - chunk)))])
  return Array.from([...head, ...tail], (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The pending import a picked file would carry on, if any (same place, size and date). */
export function pendingFor(pending: PendingImport[], library: string, picked: PickedFile): PendingImport | undefined {
  return pending.find(
    (p) => p.library === library && p.path === picked.path && p.size === picked.file.size && p.modified === picked.file.lastModified,
  )
}

export type ImportOpts = UploadOpts & {
  probe?: Probe
  /** Makes images' grid previews. */
  thumbnail?: Thumbnailer
  pending?: PendingStore
  /** When to make a playback copy (default: only when browsers can't play the original). */
  copies?: CopyMode
  maker?: CopyMaker
  /** How far making the playback copy has got, 0 to 1. */
  onConvert?: (fraction: number) => void
}

/** Whether a file gets a playback copy. */
async function copyPlanFor(maker: CopyMaker, file: File, info: MediaInfo, mode: CopyMode): Promise<CopyPlan | null> {
  if ((info.kind !== 'audio' && info.kind !== 'video') || mode === 'original') return null
  const details = await maker.details(file).catch(() => null)
  const duration = details?.duration ?? info.tags?.duration
  const src = { kind: info.kind, mime: info.mime, playable: info.streamable, size: file.size }
  return planCopy(src, mode, details && { ...details, ...(duration ? { duration } : {}) })
}

/** Makes a playback copy; null (the original alone) when this browser can't. */
async function makeCopy(maker: CopyMaker, file: Blob, plan: CopyPlan, opts: ImportOpts): Promise<MadeCopy | null> {
  if (opts.signal?.aborted) throw new UploadCancelled()
  let copy: MadeCopy | null = null
  try {
    copy = await maker.make(file, plan, {
      ...(opts.onConvert ? { onProgress: opts.onConvert } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch (e) {
    if (!opts.signal?.aborted) console.warn('hrth: no playback copy', e)
  }
  // A "lighter" copy that saves little isn't worth storing twice.
  const useless = plan.why === 'lighter' && copy && copy.blob.size > file.size * 0.8
  if (opts.signal?.aborted || useless) {
    if (copy?.name) await maker.remove(copy.name)
    if (useless) return null
    throw new UploadCancelled()
  }
  return copy
}

/**
 * Imports one file: reads it, makes a playback copy if it needs one, encrypts it, adds its
 * entry and uploads its blobs (the original, the copy, and cover art if it has some). A
 * failed or cancelled import removes its entry; one cut short (the page closed) stays in
 * `pending` to resume.
 */
export async function importFile(
  api: ImportApi,
  library: string,
  libraryKey: string,
  picked: PickedFile,
  opts: ImportOpts = {},
): Promise<LibraryEntry> {
  const info = await readMediaInfo(picked.file, opts.probe)
  const maker = opts.maker ?? browserCopies
  const plan = await copyPlanFor(maker, picked.file, info, opts.copies ?? 'auto')
  const copy = plan ? await makeCopy(maker, picked.file, plan, opts) : null
  try {
    const enc = await fileEncryptor(picked.file)
    const stream = copy ? await fileEncryptor(copy.blob) : null
    const art = info.art ? await fileEncryptor(new Blob([new Uint8Array(info.art.data)], { type: info.art.mime })) : null
    const thumb = info.kind === 'image' ? await (opts.thumbnail ?? browserThumbnail)(picked.file) : null
    const preview = thumb ? await fileEncryptor(thumb) : null
    const meta: EntryMeta = {
      v: 1,
      path: picked.path,
      size: picked.file.size,
      mime: info.mime,
      kind: info.kind,
      streamable: info.streamable,
      added: Date.now(),
      ...(info.tags ? { tags: info.tags } : {}),
      ...(plan && copy ? { copy: plan.why === 'lighter' ? 'lighter' : 'playable' } : {}),
      blobs: {
        original: enc.info,
        ...(stream && copy ? { stream: { ...stream.info, mime: copy.mime } } : {}),
        ...(art && info.art ? { art: { ...art.info, mime: info.art.mime } } : {}),
        ...(preview ? { preview: { ...preview.info, mime: 'image/jpeg' } } : {}),
      },
    }
    const entry = await api.addLibraryEntry(library, await encryptEntryMeta(libraryKey, library, meta))
    const record: PendingImport = {
      library,
      entry: entry.id,
      path: picked.path,
      size: picked.file.size,
      modified: picked.file.lastModified,
      fingerprint: await fingerprint(picked.file, enc.info.chunk),
      meta,
      uploads: {},
      done: [],
      ...(plan && copy ? { copy: { plan, ...(copy.name ? { name: copy.name } : {}) } } : {}),
    }
    try {
      await opts.pending?.put(record)
      await uploadBlobs(api, record, { original: enc, stream, art, preview }, opts)
      return entry
    } catch (e) {
      await api.deleteLibraryEntry(entry.id).catch(() => undefined)
      await opts.pending?.remove(entry.id).catch(() => undefined)
      throw e
    }
  } finally {
    if (copy?.name) await maker.remove(copy.name)
  }
}

const ROLES: BlobRole[] = ['original', 'stream', 'art', 'preview']

/** Uploads what's left of an import's blobs, keeping its record up to date. */
async function uploadBlobs(
  api: ImportApi,
  record: PendingImport,
  encs: Partial<Record<BlobRole, FileEncryptor | null>>,
  opts: UploadOpts & { pending?: PendingStore },
): Promise<void> {
  // Progress runs over the original and the copy together (art and previews are small).
  const small = (r: BlobRole) => r === 'art' || r === 'preview'
  const sizes = ROLES.map((r) => {
    const e = encs[r]
    return e && !small(r) ? ciphertextSize(e.info.size, e.info.chunk) : 0
  })
  const total = sizes.reduce((a, b) => a + b, 0) || 1
  let before = 0
  for (const [i, role] of ROLES.entries()) {
    const e = encs[role]
    if (!e) continue
    const base = before
    before += sizes[i]!
    if (record.done.includes(role)) continue
    await uploadBlob(api, record.entry, role, e, {
      ...opts,
      onProgress: small(role) ? undefined : (f) => opts.onProgress?.((base + f * sizes[i]!) / total),
      ...(record.uploads[role] ? { resume: record.uploads[role] } : {}),
      onStart: async (id) => {
        record.uploads[role] = id
        await opts.pending?.put(record)
      },
    })
    record.done.push(role)
    await opts.pending?.put(record)
  }
  await opts.pending?.remove(record.entry)
}

/**
 * The playback copy kept for an import is gone (another browser, cleared storage): a new
 * one is made and encrypted under a new key, never the old one, as its bytes differ.
 */
async function remakeCopy(api: ImportApi, r: PendingImport, file: File, maker: CopyMaker, opts: ImportOpts & { libraryKey?: string }): Promise<FileEncryptor | null> {
  if (!opts.libraryKey) throw new Error('This device doesn’t have this library’s key yet')
  const plan = r.copy?.plan ?? { kind: r.meta.kind === 'audio' ? 'audio' : 'video', why: 'unplayable' }
  const made = await makeCopy(maker, file, plan, opts)
  const old = r.uploads.stream
  if (old) await api.cancelBlobUpload(old).catch(() => undefined)
  delete r.uploads.stream
  const stream = made ? await fileEncryptor(made.blob) : null
  if (stream && made) {
    r.meta.blobs.stream = { ...stream.info, mime: made.mime }
    r.copy = { plan, ...(made.name ? { name: made.name } : {}) }
  } else {
    delete r.meta.blobs.stream
    delete r.meta.copy
    delete r.copy
  }
  await api.updateLibraryEntry(r.entry, await encryptEntryMeta(opts.libraryKey, r.library, r.meta))
  await opts.pending?.put(r)
  return stream
}

/**
 * Carries on an import cut short by a reload, with the file picked again. Refuses a file
 * that isn't byte for byte the same (its parts would be encrypted under the same key).
 */
export async function resumeImport(
  api: ImportApi,
  record: PendingImport,
  file: File,
  opts: ImportOpts & { libraryKey?: string } = {},
): Promise<void> {
  const original = record.meta.blobs.original
  if (!original) throw new Error('nothing to resume')
  const same =
    file.size === record.size && file.lastModified === record.modified && (await fingerprint(file, original.chunk)) === record.fingerprint
  if (!same) throw new Error(`${record.path} changed since it was picked: delete it and upload it again`)
  const maker = opts.maker ?? browserCopies
  const r: PendingImport = {
    ...record,
    meta: { ...record.meta, blobs: { ...record.meta.blobs } },
    uploads: { ...record.uploads },
    done: [...record.done],
  }
  const enc = await fileEncryptor(file, original.chunk, original)
  let stream: FileEncryptor | null = null
  const kept = r.meta.blobs.stream
  if (kept && !r.done.includes('stream')) {
    const copy = r.copy?.name ? await maker.open(r.copy.name) : null
    stream = copy && copy.size === kept.size ? await fileEncryptor(copy, kept.chunk, kept) : await remakeCopy(api, r, file, maker, opts)
  }
  let art: FileEncryptor | null = null
  const artInfo = r.meta.blobs.art
  if (artInfo && !r.done.includes('art')) {
    const info = await readMediaInfo(file, opts.probe)
    if (info.art) art = await fileEncryptor(new Blob([new Uint8Array(info.art.data)]), artInfo.chunk, artInfo)
  }
  // A preview drawn again would differ under the same key: go without (grids use the original).
  if (r.meta.blobs.preview && !r.done.includes('preview')) {
    delete r.meta.blobs.preview
    if (r.uploads.preview) await api.cancelBlobUpload(r.uploads.preview).catch(() => undefined)
    delete r.uploads.preview
    if (!opts.libraryKey) throw new Error('This device doesn’t have this library’s key yet')
    await api.updateLibraryEntry(r.entry, await encryptEntryMeta(opts.libraryKey, r.library, r.meta))
  }
  await uploadBlobs(api, r, { original: enc, stream, art }, opts)
  if (r.copy?.name) await maker.remove(r.copy.name)
}

/** Gives up on an import cut short: its entry and any upload go. */
export async function discardImport(api: ImportApi, record: PendingImport, pending: PendingStore, maker: CopyMaker = browserCopies): Promise<void> {
  if (record.copy?.name) await maker.remove(record.copy.name)
  for (const id of Object.values(record.uploads)) if (id) await api.cancelBlobUpload(id).catch(() => undefined)
  await api.deleteLibraryEntry(record.entry).catch((e: { status?: number }) => {
    if (e.status !== 404) throw e
  })
  await pending.remove(record.entry)
}
